import React, { useState, useEffect } from 'react';
import { Download, Bell, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show banner after a short delay if not dismissed before
      const installDismissed = localStorage.getItem('installPromptDismissed');
      if (!installDismissed) {
        setTimeout(() => setShowInstallBanner(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallBanner(false);
      toast.success('App installed successfully! You can now receive notifications.');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      if (isIOS) {
        toast.info(
          'To install: Tap the Share button, then "Add to Home Screen"',
          { duration: 6000 }
        );
      }
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('Thanks for installing! Enable notifications to stay updated.');
      requestNotificationPermission();
    } else {
      toast.info('You can install this app anytime from the menu.');
    }

    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return;
    }

    if (Notification.permission === 'granted') {
      return;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        toast.success('Notifications enabled! You\'ll be notified of project updates.');
      }
    }
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    localStorage.setItem('installPromptDismissed', 'true');
  };

  // Don't show anything if already installed
  if (isInstalled) {
    return null;
  }

  // Show banner if conditions are met
  if (showInstallBanner && (deferredPrompt || isIOS)) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-40 animate-slide-up">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow-2xl p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="bg-white/20 p-2 rounded-lg">
              <Bell className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Get Project Updates</h3>
              <p className="text-xs text-blue-100">
                {isIOS 
                  ? 'Add to Home Screen for notifications' 
                  : 'Install app to receive instant notifications'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleInstallClick}
              className="bg-white text-blue-600 hover:bg-blue-50 font-semibold"
            >
              <Download className="w-4 h-4 mr-1" />
              Install
            </Button>
            <button
              onClick={handleDismiss}
              className="text-white/80 hover:text-white p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show compact button for manual installation
  if (deferredPrompt || isIOS) {
    return (
      <Button
        onClick={handleInstallClick}
        variant="outline"
        className="w-full bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
      >
        <Download className="w-4 h-4 mr-2" />
        {isIOS ? 'Add to Home Screen' : 'Install App for Notifications'}
      </Button>
    );
  }

  return null;
};

export default InstallPWA;
